import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import Logs from '@/components/common/Logs';
import BackArrowIcon from '@/components/Icons/BackArrowIcon';
import Layout from '@/components/Layout/Layout';
import FullPageLoader from '@/components/loader/FullPageLoader';
import ApproveModal from '@/components/Modal/ApproveModal';
import Button from '@/components/ui/Button';
import { IDeleteResponse } from '@/interfaces/common.model';
import { IWfhDetail } from '@/interfaces/wfh.model';
import { RootState } from '@/redux/store';
import {
  calculateDaysDifference,
  handleStatus,
} from '@/utility/CommonFunctions';
import { capitalizeFirstLetter, showToastNotification } from '@/utility/helper';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

const WfhRequestDetail = () => {
  const { id } = useParams();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(
    null,
  );
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [refreshLogs, setRefreshLogs] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const userDetails = useSelector((state: RootState) => state?.user?.user);
  const [wfhRequestData, setWfhRequestData] = useState<IWfhDetail | null>(null);
  const navigate = useNavigate();

  const getWfhRequests = async () => {
    try {
      const Url =
        API_URL + API_ENDPOINT?.getWfhRequestDetail + `?odRequestId=${id}`;
      setIsLoading(true);
      const res = await callApi<{
        status: boolean;
        data: IWfhDetail;
        message: string;
      }>(Url, {
        method: 'GET',
      });
      setIsLoading(false);
      if (res?.status === true) {
        setWfhRequestData(res?.data);
      } else {
        showToastNotification('danger', res?.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    getWfhRequests();
  }, [getWfhRequests]);

  const handleApproveReject = async (type: string) => {
    setIsUpdating(true);
    const isHalfDay =
      wfhRequestData?.wfhDuration == 'firstHalf' ||
      wfhRequestData?.wfhDuration == 'secondHalf';
    let newStatus;
    if (type == 'approve') {
      newStatus = 1;
    } else if (type == 'reject') {
      newStatus = 2;
    }
    const submitData = {
      type: isHalfDay ? 'halfDayOD' : 'odRequest',
      id: wfhRequestData?.id,
      ...(isHalfDay ? { halfDayOdRequestStatus: newStatus } : {}),
      status: newStatus,
    };
    try {
      const response: IDeleteResponse = await callApi(
        API_URL + API_ENDPOINT.userStatusUpdate,
        {
          method: 'POST',
          data: submitData,
        },
      );
      if (response?.status) {
        setIsConfirmModalOpen(false);
        showToastNotification('success', response?.message);
        getWfhRequests();
        setRefreshLogs(true);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const submitConfirm = (actionType?: string) => {
    if (actionType === 'approve') {
      handleApproveReject('approve');
    } else if (actionType === 'reject') {
      handleApproveReject('reject');
    }
  };

  return (
    <Layout>
      {isLoading ? (
        <FullPageLoader />
      ) : (
        <>
          {' '}
          <div className="content container-fluid">
            <div className="content-head mb-5 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center justify-content-start gap-4">
                <Button
                  className="btn back-arrow "
                  onClick={() => navigate(-1)}
                >
                  <BackArrowIcon />
                </Button>
                <h1 className="mb-0">WFH Detail</h1>
              </div>
            </div>
            <div className="card manage-user-list">
              <div className="card-body">
                <ul
                  className={
                    isLoading
                      ? 'row list-unstyled mt-4 loading-skeleton'
                      : 'row list-unstyled mt-4'
                  }
                >
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Emp. Code :
                      </span>
                      {wfhRequestData?.user?.employeeCode || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Emp. Name :
                      </span>
                      {capitalizeFirstLetter(
                        wfhRequestData?.user?.name ?? '-',
                      ) || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Status :</span>
                      {handleStatus(wfhRequestData?.status ?? 0) || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Department :
                      </span>
                      {wfhRequestData?.user?.department?.name || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Designation :
                      </span>
                      {wfhRequestData?.user?.designation?.name || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Office :
                        {/* {wfhRequestData?.user?.type == 0 && ' Site :'} */}
                      </span>
                      {wfhRequestData?.user?.site?.name || '--'}
                    </p>
                  </li>
                  {/* <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Site Type :
                      </span>
                      {uniqueSiteTypes.join(', ')}
                    </p>
                  </li> */}
                  {/* <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Mode of Communication :
                      </span>
                      {wfhRequestData?.communicationMode || '--'}
                    </p>
                  </li> */}
                  {/* <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Client Name :
                      </span>
                      {wfhRequestData?.clientName || '--'}
                    </p>
                  </li> */}
                  {/* <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Contact Person :
                      </span>
                      {wfhRequestData?.contactPersonName || '--'}
                    </p>
                  </li> */}
                  {/* <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Contact Number :
                      </span>
                      {wfhRequestData?.phoneNumber || '--'}
                    </p>
                  </li> */}
                  {/* <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Location :
                      </span>
                      {wfhRequestData?.locationName || '--'}
                    </p>
                  </li> */}
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Start Date :
                      </span>
                      {(wfhRequestData?.startDate &&
                        moment(wfhRequestData?.startDate).format(
                          'DD-MM-YYYY',
                        )) ||
                        '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        End Date :
                      </span>
                      {(wfhRequestData?.endDate &&
                        moment(wfhRequestData?.endDate).format('DD-MM-YYYY')) ||
                        '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Reason :</span>
                      {wfhRequestData?.reason || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Days :-</span>
                      {wfhRequestData?.startDate && wfhRequestData?.endDate
                        ? (() => {
                            const days =
                              Number(
                                calculateDaysDifference(
                                  wfhRequestData?.startDate,
                                  wfhRequestData?.endDate,
                                  'number',
                                ),
                              ) || 0;
                            const result =
                              wfhRequestData?.wfhDuration == 'firstHalf' ||
                              wfhRequestData?.wfhDuration == 'secondHalf'
                                ? days * 0.5
                                : days;
                            return result
                              ? `${result} ${result === 1 ? 'day' : 'days'}`
                              : '';
                          })()
                        : '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        WFH Duration :-
                      </span>
                      {wfhRequestData?.wfhDuration
                        ? wfhRequestData?.wfhDuration.includes('Half')
                          ? capitalizeFirstLetter(
                              wfhRequestData?.wfhDuration,
                            )?.split('Half')?.[0] +
                            ' ' +
                            'Half'
                          : wfhRequestData?.wfhDuration
                        : '--'}
                    </p>
                  </li>
                  {/* <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Remark :</span>
                      {wfhRequestData?.remark || '--'}
                    </p>
                  </li> */}
                  {/* {wfhRequestData?.user?.type == 0 ? (
                  ""
                ) : (
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Assigned Sites :
                      </span>
                      <ul className="list-unstyled">
                        {wfhRequestData?.user?.usersites?.length > 0
                          ? wfhRequestData?.user?.usersites?.map(
                              (userSite, index) => (
                                <li key={userSite.id || index}>
                                  {userSite.site?.name || "--"}
                                </li>
                              )
                            )
                          : "-"}
                      </ul>
                    </p>
                  </li>
                )} */}
                </ul>
              </div>
              {wfhRequestData?.status == 0 &&
                wfhRequestData?.userId != userDetails?.id && (
                  <div className="card-body d-flex justify-content-end">
                    <div className="col-12 col-md-6 col-lg-4 ">
                      <span className="d-flex align-items-center gap-3 justify-content-end">
                        <>
                          <Button
                            className="btn btn-success "
                            onClick={() => {
                              setActionType('approve');
                              setIsConfirmModalOpen(true);
                            }}
                            // disabled={arDetail?.arRequestStatus == 1}
                          >
                            Approve
                          </Button>
                          <Button
                            className="btn btn-danger px-4"
                            onClick={() => {
                              setActionType('reject');
                              setIsConfirmModalOpen(true);
                            }}
                            // disabled={arDetail?.arRequestStatus == 2}
                          >
                            Reject
                          </Button>
                        </>
                      </span>
                    </div>
                  </div>
                )}
            </div>
          </div>
          <Logs
            type={'odRequest'}
            id={id ?? ''}
            refreshLogs={refreshLogs}
            setRefreshLogs={setRefreshLogs}
          />
        </>
      )}
      <ApproveModal
        isOpen={isConfirmModalOpen}
        isLoading={isUpdating}
        submitHandler={submitConfirm}
        toggle={() => setIsConfirmModalOpen(false)}
        actionType={actionType ?? ''}
      />
    </Layout>
  );
};

export default WfhRequestDetail;
